import { createContext, useContext, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export function useThemeLogic(): ThemeContextType {
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem('theme') as Theme;
    return savedTheme && ['light', 'dark'].includes(savedTheme) ? savedTheme : 'light';
  });

  const effectiveTheme = theme; // Since we only have light/dark, effective theme is always the same as theme

  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', effectiveTheme);
  }, [theme, effectiveTheme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const handleSetTheme = (newTheme: Theme) => {
    setTheme(newTheme);
  };

  return {
    theme,
    effectiveTheme,
    setTheme: handleSetTheme,
    toggleTheme,
  };
}

// Theme detection utilities
export const detectSystemTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export const getThemePreference = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem('theme');
  if (saved && ['light', 'dark'].includes(saved)) {
    return saved as Theme;
  }
  return 'light';
};

export const applyThemeTransition = () => {
  const root = document.documentElement;
  root.style.setProperty('--theme-transition-duration', '300ms');
  
  // Remove transition after animation completes
  setTimeout(() => {
    root.style.removeProperty('--theme-transition-duration');
  }, 300);
}; 